import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  GraduationCap, TrendingUp, Landmark, HeartPulse, Cpu, Users,
  Newspaper, BookOpen, Mail, Mic, Star,
  Briefcase, FileText, Building2, Globe, MessageSquare,
  Info, Bell, Navigation, ChevronRight,
} from 'lucide-react';

/* ── Panel data ───────────────────────────────────────────────── */

const NEWSROOM_PANELS = [
  {
    id: 'sector',
    type: 'big',
    HeadIcon: GraduationCap,
    headLabel: 'Sector Coverage',
    links: [
      { Icon: GraduationCap, title: 'Education',         sub: 'News, policy & innovation',          to: '/coverage/education' },
      { Icon: TrendingUp,    title: 'Business & Finance', sub: 'Markets, economy & leadership',      to: '/coverage/business-finance' },
      { Icon: Landmark,      title: 'Government',         sub: 'Policy, reform & public sector',     to: '/coverage/government' },
      { Icon: HeartPulse,    title: 'Healthcare',         sub: 'Access, care & innovation',          to: '/coverage/healthcare' },
      { Icon: Cpu,           title: 'Technology & AI',    sub: 'Innovation, data & disruption',      to: '/coverage/technology-ai' },
      { Icon: Users,         title: 'Workforce & Labor',  sub: 'Jobs, skills & the future of work',  to: '/coverage/workforce-labor' },
    ],
    footLabel: 'Explore all sectors',
    footTo: '/newsroom',
  },
  {
    id: 'editorial',
    type: 'big',
    HeadIcon: Star,
    headLabel: 'Editorial & Channels',
    links: [
      { Icon: Newspaper, title: 'Newsroom',          sub: 'Daily news & briefings',    to: '/newsroom' },
      { Icon: BookOpen,  title: 'Magazine',           sub: 'In-depth features & analysis', to: '/magazine' },
      { Icon: Mail,      title: 'Newsletter',         sub: 'The 3-minute signal',       to: '/newsletter' },
      { Icon: Mic,       title: 'Podcast',            sub: 'Conversations that matter', to: '/podcast' },
      { Icon: Star,      title: 'Exemplary Leaders',     sub: 'Profiles & insights',       modal: 'el' },
      { Icon: Building2, title: 'Organization Spotlight', sub: 'Sector features & programs', modal: 'oif' },
    ],
    footLabel: 'Browse all channels',
    footTo: '/newsroom',
  },
  {
    id: 'career',
    type: 'big',
    HeadIcon: Briefcase,
    headLabel: 'Career & Platform',
    links: [
      { Icon: Briefcase,     title: 'Career Hub',             sub: 'Browse jobs & opportunities',  to: '/career-hub' },
      { Icon: FileText,      title: 'First Briefcase',        sub: 'Find your path — 4 questions', to: '/first-briefcase' },
      { Icon: Building2,     title: 'For Employers',          sub: 'Post jobs & find talent',      to: '/employer/register' },
      { Icon: Users,         title: 'Job Seekers',            sub: 'Track your applications',      to: '/seeker/login' },
      { Icon: Globe,         title: 'About Opportunity Post', sub: 'Our mission & impact',          to: '/about' },
    ],
    footLabel: 'Browse all jobs',
    footTo: '/career-hub',
  },
  { id: 'stories-nr', type: 'stories',  headLabel: 'Opportunity Insider', footLabel: 'Download Intelligence', hideOverlay: true, cardGap: '3px' },
  { id: 'mag-nr',     type: 'magazine', headLabel: 'Latest Issue', hideOverlay: true },
];

const ABOUT_PANELS = [
  { id: 'stories-ab', type: 'stories',  headLabel: 'Top Stories' },
  { id: 'mag-ab',     type: 'magazine', headLabel: 'Magazine Feature' },
  {
    id: 'company',
    type: 'big',
    HeadIcon: Building2,
    headLabel: 'Company',
    links: [
      { Icon: Info,          title: 'About',     sub: 'Our mission & editorial model',  to: '/about' },
      { Icon: Newspaper,     title: 'Newsroom',  sub: 'Latest coverage & briefings',    to: '/newsroom' },
      { Icon: MessageSquare, title: 'Contact',   sub: 'Get in touch',                   to: '/contact' },
      { Icon: Bell,          title: 'Subscribe', sub: 'Daily opportunity briefing',     to: '/subscribe' },
    ],
    footLabel: 'Learn more',
    footTo: '/about',
  },
  {
    id: 'partner',
    type: 'big',
    HeadIcon: Users,
    headLabel: 'Partner & Visibility',
    links: [
      { Icon: BookOpen,  title: 'Media Kit',         sub: 'Press resources & brand assets',      to: '/media-kit' },
      { Icon: Building2, title: 'Featured Employer', sub: 'Employer visibility programs',        disabled: true },
      { Icon: Star,      title: 'Exemplary Leaders', sub: 'Profiles & recognition',              disabled: true },
      { Icon: Globe,     title: 'Sponsor an Issue',  sub: 'Magazine & newsletter sponsorships',  disabled: true },
    ],
  },
  {
    id: 'platform',
    type: 'big',
    HeadIcon: Navigation,
    headLabel: 'Platform Paths',
    links: [
      { Icon: BookOpen,  title: 'Magazine',   sub: 'In-depth features & analysis', to: '/magazine' },
      { Icon: Briefcase, title: 'Career Hub', sub: 'Jobs & career resources',      to: '/career-hub' },
      { Icon: Mail,      title: 'Newsletter', sub: 'The 3-minute signal',           to: '/newsletter' },
      { Icon: Mic,       title: 'Podcast',    sub: 'Conversations that matter',     to: '/podcast' },
      { Icon: Bell,      title: 'Subscribe',  sub: 'Start your free subscription', to: '/subscribe' },
    ],
    footLabel: 'Explore platform',
    footTo: '/newsroom',
  },
];

const STORIES = [
  { title: 'Policy Shift Reshapes Education',  bg: '#0d1f30' },
  { title: 'AI Investment Hits New Highs',      bg: '#0c1e18' },
  { title: 'Healthcare Access Expands',         bg: '#1a1028' },
  { title: 'Workforce Demands Grow',            bg: '#111508' },
];

/* ── Resolve the correct link dest from a CMS slot ── */
function slotHref(slot) {
  if (!slot) return null;
  const lt = slot.link_type || 'internal';
  if (lt === 'internal')  return slot.cta_url      || null;
  if (lt === 'external')  return slot.external_url  || null;
  if (lt === 'document')  return slot.document_url  || null;
  if (lt === 'youtube')   return slot.youtube_url   || null;
  if (lt === 'video')     return slot.video_url     || null;
  if (lt === 'resource')  return slot.resource_url  || null;
  return slot.cta_url || null;
}

/* ── Resolve display image: custom thumbnail takes priority ── */
function resolveImage(slot) {
  if (slot?.use_custom_thumbnail && slot?.thumbnail_image_url?.trim()) {
    return slot.thumbnail_image_url.trim();
  }
  return slot?.image_url?.trim() || null;
}

/* ── Resolve zone3 link destination ── */
function z3Href(z3) {
  const lt = z3?.feature_link_type || 'internal';
  if (lt === 'internal')  return z3?.feature_url           || '/magazine';
  if (lt === 'external')  return z3?.feature_external_url  || null;
  if (lt === 'document')  return z3?.feature_document_url  || null;
  if (lt === 'youtube')   return z3?.feature_youtube_url   || null;
  if (lt === 'video')     return z3?.feature_video_url     || null;
  if (lt === 'resource')  return z3?.feature_resource_url  || null;
  return z3?.feature_url || '/magazine';
}

/* ── Resolve zone3 display image ── */
function resolveZ3Image(z3) {
  if (z3?.feature_use_custom_thumbnail && z3?.feature_thumbnail_image_url?.trim()) {
    return z3.feature_thumbnail_image_url.trim();
  }
  return z3?.feature_image_url?.trim() || null;
}

/* ── Sub-components ───────────────────────────────────────────── */

function PanelHead({ Icon, label }) {
  return (
    <div className="mm-ph">
      {Icon && (
        <span className="mm-ph-box">
          <Icon size={12} strokeWidth={2} />
        </span>
      )}
      <span className="mm-ph-label">{label}</span>
    </div>
  );
}

function BigPanel({ panel, onModalClick }) {
  return (
    <div className="mm-panel mm-panel--big" data-testid={`mm-panel-${panel.id}`}>
      <PanelHead Icon={panel.HeadIcon} label={panel.headLabel} />

      <div className="mm-pc">
        {panel.links.map((lnk) => {
          const row = (
            <>
              <span className="mm-lnk-icon"><lnk.Icon size={14} strokeWidth={1.8} /></span>
              <span className="mm-lnk-body">
                <span className="mm-lnk-t">
                  {lnk.title}
                  {lnk.disabled && <em className="mm-soon">Coming Soon</em>}
                </span>
                <span className="mm-lnk-s">{lnk.sub}</span>
              </span>
              {!lnk.disabled && !lnk.modal && <ChevronRight size={10} strokeWidth={2.2} className="mm-lnk-arr" />}
              {lnk.modal && <ChevronRight size={10} strokeWidth={2.2} className="mm-lnk-arr" />}
            </>
          );

          if (lnk.modal) return (
            <button
              key={lnk.title}
              className="mm-lnk"
              data-testid="mm-link-exemplary-leaders"
              onClick={() => window.dispatchEvent(new CustomEvent(lnk.modal === 'oif' ? 'open-oif-modal' : 'open-el-modal'))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', padding: 0, font: 'inherit' }}
            >
              {row}
            </button>
          );

          return lnk.disabled ? (
            <span key={lnk.title} className="mm-lnk mm-lnk--disabled">{row}</span>
          ) : (
            <Link
              key={lnk.title}
              to={lnk.to}
              className="mm-lnk"
              data-testid={`mm-link-${lnk.to.replace(/\//g, '-').replace(/^-/, '')}`}
            >
              {row}
            </Link>
          );
        })}
      </div>

      {panel.footLabel && (
        <div className="mm-pf">
          <Link to={panel.footTo} className="mm-pf-cta" data-testid={`mm-foot-${panel.id}`}>
            {panel.footLabel} →
          </Link>
        </div>
      )}
    </div>
  );
}

function StoriesPanel({ panel, cmsData }) {
  const slots = [
    cmsData?.story_1 ? { ...cmsData.story_1 } : { title: STORIES[0].title, bg_color: STORIES[0].bg, is_active: true },
    cmsData?.story_2 ? { ...cmsData.story_2 } : { title: STORIES[1].title, bg_color: STORIES[1].bg, is_active: true },
  ].filter(s => s.is_active !== false);

  return (
    <div className="mm-panel mm-panel--small" data-testid={`mm-panel-${panel.id}`}>
      <PanelHead Icon={Newspaper} label={panel.headLabel} />

      <div className="mm-pc mm-pc--media">
        <div className="mm-stories" style={panel.cardGap ? { gap: panel.cardGap } : undefined}>
          {slots.map((s, i) => {
            const img = resolveImage(s);
            const bgStyle = img
              ? { backgroundImage: `url(${img})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
              : { background: s.bg_color || STORIES[i]?.bg || '#0d1f30' };
            const dest  = slotHref(s);
            const isExt = s.link_type && s.link_type !== 'internal';
            const inner = panel.hideOverlay ? null : (
              <>
                <div className="mm-story-grad" />
                <p className="mm-story-title">{s.title || STORIES[i]?.title}</p>
              </>
            );
            if (dest && !isExt) return (
              <Link key={i} to={dest} className="mm-story" style={bgStyle}>{inner}</Link>
            );
            if (dest && isExt) return (
              <a key={i} href={dest} target="_blank" rel="noopener noreferrer" className="mm-story" style={{ ...bgStyle, textDecoration: 'none' }}>{inner}</a>
            );
            return <div key={i} className="mm-story" style={bgStyle}>{inner}</div>;
          })}
        </div>
      </div>

      <div className="mm-pf">
        <Link to="/newsroom" className="mm-pf-cta" data-testid={`mm-foot-${panel.id}`}>
          {panel.footLabel || 'View all top stories'} →
        </Link>
      </div>
    </div>
  );
}

function MagazinePanel({ panel, cmsData }) {
  const z3      = cmsData?.zone3 || {};
  const eyebrow = z3.feature_label    || 'Opportunity Post';
  const title   = z3.feature_title    || 'The Future of Work';
  const sub     = z3.feature_subtitle || 'Education · Innovation · Opportunity';
  const dest    = z3Href(z3);
  const isExt   = z3.feature_link_type && z3.feature_link_type !== 'internal';

  const img = resolveZ3Image(z3);
  const bgStyle = img
    ? { backgroundImage: `url(${img})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }
    : {};

  const cardInner = panel.hideOverlay ? null : (
    <>
      <div className="mm-mag-stripe" />
      <span className="mm-mag-ey">{eyebrow}</span>
      <span className="mm-mag-title">{title}</span>
      <span className="mm-mag-sub">{sub}</span>
    </>
  );

  const card = isExt ? (
    <a href={dest || '#'} target="_blank" rel="noopener noreferrer"
      className="mm-mag" style={{ ...bgStyle, textDecoration: 'none' }}
      data-testid={`mm-mag-card-${panel.id}`}>
      {cardInner}
    </a>
  ) : (
    <Link to={dest || '/magazine'} className="mm-mag" style={bgStyle}
      data-testid={`mm-mag-card-${panel.id}`}>
      {cardInner}
    </Link>
  );

  return (
    <div className="mm-panel mm-panel--small" data-testid={`mm-panel-${panel.id}`}>
      <PanelHead Icon={BookOpen} label={panel.headLabel} />
      <div className="mm-pc mm-pc--media">{card}</div>
      <div className="mm-pf">
        {isExt ? (
          <a href={dest || '#'} target="_blank" rel="noopener noreferrer"
            className="mm-pf-cta" data-testid={`mm-foot-${panel.id}`}>
            {z3.feature_cta_label || 'Read current issue'} →
          </a>
        ) : (
          <Link to={dest || '/magazine'} className="mm-pf-cta" data-testid={`mm-foot-${panel.id}`}>
            {z3.feature_cta_label || 'Read current issue'} →
          </Link>
        )}
      </div>
    </div>
  );
}

/* ── Main export ──────────────────────────────────────────────── */

export const MegaMenu = ({ activeMenu, onMouseEnter, onMouseLeave }) => {
  const panels  = activeMenu === 'newsroom' ? NEWSROOM_PANELS : ABOUT_PANELS;
  const trayMod = activeMenu === 'newsroom' ? 'mm-tray--nr' : 'mm-tray--ab';

  const [configs, setConfigs] = useState({});

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL + '/api';
    Promise.all([
      fetch(`${base}/mega-menu/newsroom`).then(r => r.json()).catch(() => null),
      fetch(`${base}/mega-menu/about`).then(r => r.json()).catch(() => null),
    ]).then(([nr, ab]) => setConfigs({ newsroom: nr, about: ab }));
  }, []);

  const cmsData = configs[activeMenu] || null;

  return (
    <div
      className="op-mega-panel"
        data-testid={`mega-menu-${activeMenu}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role="navigation"
        aria-label={activeMenu === 'newsroom' ? 'Newsroom navigation' : 'About navigation'}
      >
        <div className={`mm-tray ${trayMod}`}>
          {panels.map((p) => {
            if (p.type === 'big')      return <BigPanel      key={p.id} panel={p} />;
            if (p.type === 'stories')  return <StoriesPanel  key={p.id} panel={p} cmsData={cmsData} />;
            if (p.type === 'magazine') return <MagazinePanel key={p.id} panel={p} cmsData={cmsData} />;
            return null;
          })}
        </div>
      </div>
  );
};
